import { Storage } from "@/services/Storage";
import axios from "axios";
import { responses } from "@/utils/responses";
import router from "@/router";
import * as Sentry from '@sentry/vue';
import connectivityMonitor from '@/services/connectivityMonitor';
import loginOutbox from '@/services/loginOutbox';
import { supportStaffGate } from '@/services/supportStaffGate';
// docs/FRONTEND.md § Sales-write user field — #26328 / Sentry 1V+1E
import { assertSalesWriteUser } from '@/services/salesWriteUser';

export default {
    name: 'user',
    state: {
        admin: '',
        currentUser: {
            Type: "",
            Username: "",
            id: "",
            Surname: "",
            Name: "",
            Password: ""
        },
        securities: [],
        users: [],
        allUsers: [],
        supervisor: null,
    },
    getters: {
        getCurrentUser: (state) => state.currentUser,
        getAllUsers: (state) => state.allUsers,
        getAdmin: (state) => {
            if (state.admin) {
                return JSON.parse(window.atob(state.admin))
            }
        },
        getUsers: (state) => state.users,
        getUserByPass: (state) => async (password) => {
            // DEPRECATED after password-encryption rollout (2026-06-18).
            // The cached users list no longer carries `Password`, so this
            // compare always returns undefined. Use `dispatch('posLogin')`
            // (backend PIN verification) instead.
            console.warn('[user_module] getUserByPass is deprecated after the password-encryption rollout. Use dispatch("posLogin").');
            state.users = await Storage.getAllUsers();
            return state.users.find(u => u.Password && u.Password.toLowerCase() === password.toLowerCase());
        },

        countNonLytSoftUsers: async (state) => {
            state.users = await Storage.getAllUsers();
            return state.users.filter(user => user.Username !== 'Admin').length;
        },

        getUserByID: (state) => async (id) => {
            state.users = await Storage.getAllUsers();
            return state.users.find(u => u.id === id);
        },
        getSecurities: (state) => state.securities,
        getSupervisor: (state) => state.supervisor,
    },
    mutations: {
        setAdmin: async (state, data) => {
            state.admin = window.btoa(JSON.stringify(data));
        },
        /**
         * Stores the authenticated cashier and synchronizes the minimal
         * identity context used to correlate Sentry events. Email is omitted
         * because POS telemetry defaults to no user PII collection.
         * @param {Object} state User Vuex module state.
         * @param {Object|null} data Authenticated cashier or empty logout state.
         */
        setCurrentUser: async (state, data) => {
            state.currentUser = data;
            const id = data && (data.UserID || data.id);
            const username = data && data.Username && String(data.Username).trim();
            if (id && username) {
                Sentry.setUser({ id: String(id), username });
            } else {
                Sentry.setUser(null);
            }
        },
        setAllSecurities: async (state, data) => {
            if (!data) data = await Storage.getAllSecurities();
            state.securities = data;
        },
        setAllUsers: async (state, data) => {
            if (!data) {
                data = await Storage.getAllUsers();
            }
            state.users = data
        },
        setSupervisor(state, supervisor) {
            state.supervisor = supervisor;
        },
        // Mutations added for the session-based deep link receiver flow
        // (2026-06-24). They mirror the Online BackOffice Front commit
        // order so the router guard sees a fully-populated state. The
        // Retail POS uses the "adminUser" slot for the mobileUser payload
        // (see performAutoLogin / setAdminUser). The store-level
        // setAllStores / setCurrentStore / setDefaultStore mutations in
        // store/index.js handle persistence for those, so we just forward
        // by committing those root-level names.
        setMobileUser: async (state, payload) => {
            // Persist into the adminUser slot via Storage helper. The
            // synchronous state.admin update mirrors setAdmin.
            state.admin = payload ? window.btoa(JSON.stringify(payload)) : '';
            try { await Storage.setAdminUser(payload || {}); } catch (_) {}
        },
        setAllStores: async (state, payload) => {
            // Forward to Storage.setAllStores which commits setRegisteredStores.
            await Storage.setAllStores(payload);
        },
        setCurrentStore: async (state, payload) => {
            if (payload) await Storage.setDefaultStore(payload);
        },
    },
    actions: {
        async recordlogin(context, username) {
            let device = await Storage.getDeviceSettings();
            device = device.PC;
            let store = await Storage.getDefaultStore();
            if (!store) return;
            // Fail-fast if cashier identity missing — never omit username (#26328)
            const safeUsername = assertSalesWriteUser(username, 'user_module.recordlogin');
            const payload = {
                username: safeUsername,
                platform: 'Lytning POS',
                pc: device,
            }
            
            // Check if online
            const isOnline = connectivityMonitor.getStatus();
            
            if (!isOnline) {
                // Offline: Queue login record for later sync
                ////console.log('[User Module] Offline - queueing login record for sync');
                const loginId = await loginOutbox.enqueueLogin(username, 'Lytning POS', device, store.StoreDB);
                if (loginId) {
                    //console.log('[User Module] Login record queued:', loginId);
                } else {
                    console.warn('[User Module] Failed to queue login record');
                }
                return;
            }
            
            // Online: Post to server
            return axios.post(serverApi + `supervisorOverrides/${store.StoreDB}/recordLogin`, payload)
                .catch(async (err) => {
                    // Network error: Queue for offline sync
                    console.warn('[User Module] Failed to record login, queueing for sync:', err.message);
                    const loginId = await loginOutbox.enqueueLogin(username, 'Lytning POS', device, store.StoreDB);
                    if (loginId) {
                        //console.log('[User Module] Login record queued after failure:', loginId);
                    }
                    // Don't throw error - allow login to proceed
                });
        },

        async recordLogout(context, username) {
            let device = await Storage.getDeviceSettings();
            device = device.PC;
            let store = await Storage.getDefaultStore();
            if (!store) return;
            const payload = {
                username: username,
                platform: 'Lytning POS',
                pc: device,
            }
            // If offline, skip remote call gracefully
            const isOnline = connectivityMonitor.getStatus();
            if (!isOnline) {
                //console.log('[User Module] Offline - skipping recordLogout API call');
                // D3: still clear the recorded support staff on logout so we
                // don't attribute any later (none expected, defence in depth)
                // actions to a staff no longer on shift.
                supportStaffGate.clearRecordedSupportAgent();
                return;
            }
            // Online: try to post, swallow errors so logout never blocks
            return axios.post(serverApi + `supervisorOverrides/${store.StoreDB}/recordLogout`, payload)
                .catch((err) => {
                    console.warn('[User Module] Failed to record logout:', err?.message || err);
                    // Do not throw - allow logout to proceed
                })
                .finally(() => {
                    // D3: clear the recorded support staff on logout so we
                    // don't attribute any later actions to a staff no longer
                    // on shift.
                    supportStaffGate.clearRecordedSupportAgent();
                });
        },

        getAllUsers: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            if (!store) {
                let route = 'login';
                let allStores = context.getters.getRegisteredStores;
                if (allStores.length > 0) {
                    route = 'setup';
                }
                responses.showInfo(`Welcome , Please Login`)
                return router.push({ name: route })
            }
            
            // Check if online
            const isOnline = connectivityMonitor.getStatus();
            
            if (!isOnline) {
                // Offline: Load from cache
                //console.log('[User Module] Offline - loading users from cache');
                const cachedUsers = await Storage.getAllUsers();
                if (cachedUsers && cachedUsers.length > 0) {
                    context.commit('setAllUsers', cachedUsers);
                    return cachedUsers;
                } else {
                    responses.showWarning('No cached user data available. Please connect to network.');
                    return [];
                }
            }
            
            // Online: Fetch from server
            return axios.get(franchiseTomcatApi + `employee/${store.StoreDB}/getAll`)
                .then(async ({ data }) => {
                    const filteredUsers = data.filter(user => !user.archived);
                    await Storage.setAllUsers(filteredUsers);
                    context.commit('setAllUsers', filteredUsers);
                    return data;
                }).catch(async (err) => {
                    // Network error: Fallback to cache
                    console.warn('[User Module] Failed to fetch users from server, using cache');
                    const cachedUsers = await Storage.getAllUsers();
                    if (cachedUsers && cachedUsers.length > 0) {
                        context.commit('setAllUsers', cachedUsers);
                        Toast.fire({
                            icon: 'warning',
                            title: 'Using cached user data'
                        });
                        return cachedUsers;
                    }
                    return errorHandler.tomcatError(err);
                })
        },

        posLogin: async (context, data) => {
            // Backend PIN verification.
            //
            // After the password-encryption rollout (Argon2id hashes stored
            // in emp.Password) the client can no longer compare the typed
            // PIN against the cached users list - hashes are one-way.
            // Verification has to move server-side, so the POS now calls
            // POST {franchiseTomcatApi}/posAuth/{DB}/login which uses
            // PasswordEncryptionService.verify() under the hood.
            //
            // The endpoint also returns the employee id and username so the
            // frontend can update the currentUser in storage exactly like
            // the old local-compare flow did.
            try {
                let store = await Storage.getDefaultStore();
                if (!store || !store.StoreDB) {
                    throw Object.assign(new Error('No store selected'), {
                        response: { status: 401, data: { message: 'No store selected' } }
                    });
                }
                const { data: resp } = await axios.post(
                    franchiseTomcatApi + `posAuth/${store.StoreDB}/login`,
                    { password: data }
                );
                const respSessionIdPrefix = resp && resp.sessionId && typeof resp.sessionId === 'string' ? resp.sessionId.slice(0, 8) + '...' : 'none';
                // eslint-disable-next-line no-console
                console.log(`[deeplink][posLogin] server response success=${!!(resp && resp.success)} sessionIdPrefix=${respSessionIdPrefix} storeDb=${store.StoreDB}`);
                if (!resp || !resp.success) {
                    // 401-shaped so tomcatError shows auth message, not 500 support Swal
                    const msg = (resp && (resp.message || resp.error || resp.Message || resp.Error)) || 'Incorrect PIN';
                    throw Object.assign(new Error(msg), {
                        response: { status: 401, data: resp || { message: msg } }
                    });
                }
                // Persist the deeplink session payload so the
                // backoffice-module deeplink opener can build a
                // `LytningCore://open?session=…&store=…&page=…` URL
                // without the plaintext password. Clear any stale
                // session first so a switch between stores on the same
                // device does not leak the previous store's token.
                try {
                    await Storage.clearCurrentSession();
                    // eslint-disable-next-line no-console
                    console.log('[deeplink][posLogin] cleared stale session');
                    await Storage.setCurrentSession({
                        sessionId: resp.sessionId,
                        sessionExpiresAt: resp.sessionExpiresAt,
                        username: resp.username,
                        empId: resp.empId,
                    }, store.StoreDB);
                    // eslint-disable-next-line no-console
                    console.log('[deeplink][posLogin] persisted new session ok');
                } catch (sessionErr) {
                    // Persistence is best-effort - do not block login.
                    // eslint-disable-next-line no-console
                    console.warn('[user_module][posLogin] failed to persist deeplink session', sessionErr);
                }
                // Find the matching cached user so we keep the same shape
                // for downstream callers (setCurrentUser, getAllStock, etc.).
                const currentUser = context.getters.getUsers.find(
                    u => u.id === resp.empId || (u.Username && resp.username && u.Username.toLowerCase() === resp.username.toLowerCase())
                );
                if (currentUser) {
                    await Storage.setCurrentUser(currentUser);
                    // Stock is loaded at app boot (syncData) and by SyncService
                    // on a timer — not on every PIN. Full skipDelta pull after
                    // login blocked the POS with a long catalogue download.
                    return { isValid: true, username: currentUser.Username };
                }
                // Cache miss (offline cache older than current store roster).
                // Still treat login as valid - return the username from the
                // server response. setCurrentUser will be reconciled on next
                // getAllUsers sync.
                return { isValid: true, username: resp.username };
            } catch (err) {
                const status = err && err.response && err.response.status;

                // 404 fallback: server endpoint not registered on this
                // deployment (legacy franchise). Compare the typed PIN
                // locally against the cached users list. Legacy stores
                // that pre-date the Argon2id rollout (2026-06-18) still
                // carry the plaintext Password field and will match;
                // newer stores will fail closed because their cached
                // Password is empty.
                if (status === 404) {
                    const typedPin = data == null ? '' : String(data);
                    const matched = context.getters.getUsers.find(
                        u => u.Password && typeof u.Password === 'string'
                            && u.Password.toLowerCase() === typedPin.toLowerCase()
                    );
                    if (matched) {
                        try {
                            await Storage.setCurrentUser(matched);
                        } catch (persistErr) {
                            // eslint-disable-next-line no-console
                            console.warn('[user_module][posLogin] legacy fallback: failed to persist current user', persistErr);
                        }
                        return { isValid: true, username: matched.Username };
                    }
                    throw Object.assign(new Error('Incorrect PIN'), {
                        response: { status: 401, data: { message: 'Incorrect PIN' } }
                    });
                }

                // 401 wrong PIN, network, 5xx — UI calls errorHandler.tomcatError(err)
                throw err;
            }
        },

        async editEmployee(context, payload) {
            let store = await Storage.getDefaultStore();
            return axios.put(franchiseTomcatApi + `employee/${store.StoreDB}/${payload.id}`, payload)
                .then(() => context.dispatch(`getAllUsers`))
                .catch((err) => errorHandler.tomcatError(err))
        },

        createEmployee: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            return axios.post(franchiseTomcatApi + `employee/${store.StoreDB}`, payload)
                .then(() => context.dispatch('getAllUsers'))
                .catch((err) => errorHandler.tomcatError(err))
        },

        forceCashUp: async () => {
            let store = await Storage.getDefaultStore();
            let currentUser = await Storage.getCurrentUser()
            let settings = await Storage.getDeviceSettings()
            return axios.get(franchiseTomcatApi + `cashup/${store.StoreDB}/forceCashup?user=` + currentUser.Username + '&pc=' + settings.PC)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },

        /**
         * Session-based deep link receiver action.
         *
         * <p>This is the receiver-side counterpart to the source-side
         * {@code openOnlineBackOffice} action. The source app mints an
         * opaque 64-char hex sessionId at login (Franchise Online Server V2,
         * {@code POST /posAuth/{DB}/login}) and embeds
         * {@code ?session=<id>&store=<DB>&page=<name>} in the URL. We
         * delegate to {@code deepLinkLogin} which calls
         * {@code GET /posAuth/{DB}/session/{id}} and hydrates the user
         * from the response. No password ever crosses the deeplink boundary.
         *
         * <p>Mirrors the Online BackOffice Front commit order:
         * setAllStores -> setMobileUser -> setCurrentStore -> setCurrentUser.
         *
         * @param {Object} context - Vuex action context
         * @param {Object} payload - { sessionId, store, page, device }
         * @returns {Promise<{success: boolean, error?: string}>}
         */
        sessionLogin: async (context, payload) => {
            const { sessionId, store, page } = payload || {};
            return context.dispatch('deepLinkLogin', {
                sessionId,
                store,
                page: page ? (typeof page === 'string' ? { name: page } : page) : null,
                device: payload && payload.device,
            });
        },

        /**
         * Deep link login action - handles authentication from a
         * session-bearing deep link.
         *
         * <p>Two payload shapes are accepted:
         * <ul>
         *   <li><b>New (preferred):</b> {@code { sessionId, store, page }}
         *       - calls the GET endpoint and hydrates from the response.</li>
         * </ul>
         *
         * <p>Works when the app is already running (replaces the existing
         * session) and on cold start (via the pending-deep-link cold path
         * handled by deepLinkService.handleDeepLinkSession).
         */
        deepLinkLogin: async (context, payload) => {
            const { sessionId, store, page } = payload || {};
            console.log('========== [deepLinkLogin] START ==========');
            console.log('[deepLinkLogin] sessionId:', sessionId);
            console.log('[deepLinkLogin] Store:', store?.StoreName || store?.StoreDB);
            console.log('[deepLinkLogin] Target page:', page?.name || page);
            console.log('[deepLinkLogin] ========================================');

            try {
                if (!sessionId || !store || !store.StoreDB) {
                    throw new Error('Deep link payload missing sessionId or storeDB');
                }

                if (!franchiseTomcatApi) {
                    throw new Error('franchiseTomcatApi is not configured');
                }

                const { data: resp } = await axios.get(
                    `${franchiseTomcatApi}posAuth/${store.StoreDB}/session/${sessionId}`,
                    { timeout: 8000 }
                );
                if (!resp || !resp.success) {
                    throw new Error('Session lookup returned failure');
                }
                console.log('[deepLinkLogin] Resolved user:', resp.username, '(id=' + resp.userId + ')');

                // Clear any existing session state, then re-hydrate with
                // the resolved user. Mirrors Online BackOffice Front
                // hydrateFromDeeplinkSession commit order.
                await context.dispatch('clearStoreState');

                const hydrated = {
                    id: resp.userId,
                    Username: resp.username,
                    Name: resp.name || resp.username,
                    Email: resp.email,
                    Type: resp.type,
                    isAdmin: !!resp.isAdmin,
                };

                const mobileUser = {
                    id: hydrated.id,
                    email: hydrated.Email,
                    mainCustomerID: store.mainCustomerID || store.CustomerID || null,
                    CustomerID: store.CustomerID || null,
                    fromDeeplink: true,
                };

                await context.commit('setAllStores', [store]);
                await context.commit('setMobileUser', mobileUser);
                await context.commit('setCurrentStore', store);
                await Storage.setCurrentUser(hydrated);
                await context.commit('setCurrentUser', hydrated);

                // Refresh the user list so the rest of the app sees a
                // fully-populated roster.
                try {
                    await context.dispatch('getAllUsers');
                } catch (e) {
                    console.warn('[deepLinkLogin] getAllUsers refresh failed (non-fatal):', e);
                }

                return { success: true };
            } catch (error) {
                console.error('[deepLinkLogin] failed:', error);
                return { success: false, error: error.message || String(error) };
            }
        },

        /**
         * Clear all store-related Vuex + storage state. Used by the deep
         * link receiver to wipe the previous session before re-hydrating
         * from a new sessionId.
         */
        clearStoreState: async (context) => {
            try {
                await Storage.clearCurrentSession();
            } catch (_) {}
            try {
                await context.commit('setCurrentUser', {
                    Type: '',
                    Username: '',
                    id: '',
                    Surname: '',
                    Name: '',
                    Password: '',
                });
            } catch (_) {}
            try { await context.commit('setMobileUser', null); } catch (_) {}
            try { await Storage.setAdminUser({}); } catch (_) {}
            try { await context.commit('setDefaultStore', null); } catch (_) {}
            try { await context.commit('setRegisteredStores', []); } catch (_) {}
            try { await Storage.setAllStores([]); } catch (_) {}
        },

        async generateStoreOTP() {
            let store = await Storage.getDefaultStore();
            return axios.post(franchiseTomcatApi + `stores/${store.StoreDB}/generateOTP`)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },
        /**
         * Resolve whether the current user's security group grants the
         * named permission (`security`). The keys live in the backend
         * `securities` collection and are looked up by name on the
         * matching group object (e.g. `group['StockTake']`,
         * `group['StockTransfer']`).
         *
         * NOTE: This is the silent / hot-path check — no popup. The
         * Admin support-staff + OTP gate lives in
         * `src/components/UserSecurityChecker.vue#checkSecurity`, where
         * it fires when an `Admin` user enters their PIN in the
         * "Supervisor Approval Required" modal (security safeguard per
         * Felix: "in case someone knows the admin's password and they are
         * not logged in as admin"). See
         * `docs/exec-plans/completed/2026-06-23-admin-otp-gate-validateSecurityPermissionNoPrompt.md`.
         *
         * @param {object} context - Vuex action context
         * @param {string} security - permission key to check (e.g. 'StockTake', 'StockTransfer')
         * @returns {Promise<boolean|object>} true when allowed, false when
         *      denied, or `{ supervisor }` when the permission matches a
         *      void-style flow that needs supervisor override tracking
         */
        validateSecurityPermissionNoPrompt: async (context, security) => {
            let securities = await Storage.getAllSecurities();
            let user = await Storage.getCurrentUser();
            let userType = (user && user.Type) || '';
            let group = securities.find(sec => (sec.GroupName || '').toLowerCase() === userType.toLowerCase());
            if (!group) {
                console.warn(`No security group found`)
                return false;
            }
            return group[security];
        },

        /**
         * Resolve whether the current user's security group grants the
         * named permission (`security`). The keys live in the backend
         * `securities` collection and are looked up by name on the
         * matching group object (e.g. `group['StockTake']`,
         * `group['StockTransfer']`).
         *
         * <h3>Three-way resolution</h3>
         * <ol>
         *   <li><b>Allowed outright</b> — `group[security] === true`.
         *       Returns `true` (or, for void-style permissions whose key
         *       contains "void", returns `{ supervisor: <username> }`
         *       so the caller can stamp the void record with the
         *       authorising cashier username).</li>
         *   <li><b>No matching group</b> — the current user has no
         *       `securities` entry for their `Type`. Shows a SweetAlert
         *       warning ("No security group found") and resolves `false`.
         *       No popup is shown.</li>
         *   <li><b>Denied, supervisor may authorise</b> — `group[security]`
         *       is falsy. Calls `$utils.hideLoading()` to clear any
         *       in-progress spinner, then dispatches
         *       {@link ./user/user_security_module.js#checkUserSecurity `checkUserSecurity(security)`}
         *       which opens the <b>Supervisor Approval Required</b>
         *       modal (`src/components/UserSecurityChecker.vue`).
         *       That modal renders a password input for the supervisor's
         *       PIN, calls `posLogin` (backend Argon2id verification at
         *       `<franchiseTomcatApi>/posAuth/{DB}/login`), looks up
         *       the resolved supervisor in the cached users list, and
         *       confirms their security group actually grants the
         *       requested permission. If the supervisor is `Admin`,
         *       it additionally fires the admin support-staff OTP gate
         *       (`AdminSupportStaffModal` → `AdminOtpInput`) — a
         *       safeguard for the case "someone knows the admin's
         *       password and they are not logged in as admin" (see
         *       `docs/exec-plans/completed/2026-06-23-admin-otp-gate-validateSecurityPermissionNoPrompt.md`).
         *       On success the modal POSTs the override to
         *       `<serverApi>/supervisorOverrides/{DB}` for audit and
         *       persists `value.supervisor` via `Storage.setSupervisor()`.</li>
         * </ol>
         *
         * <h3>Caller contract</h3>
         * Callers MUST `await` the returned promise before allowing the
         * gated action (void, stock-take, transfer, etc.) to proceed.
         * The promise resolves `true` only when the user (or an
         * authorised supervisor) has explicitly approved; otherwise it
         * resolves `false` and the gated UI must abort. While the
         * supervisor modal is open, the cashier is blocked at the gate
         * — no action proceeds until the supervisor types their PIN
         * and clicks <em>Authorize</em> (or cancels the modal).
         *
         * <h3>Backend prerequisite</h3>
         * `StockTransfer` must exist as a column on the `securities`
         * group document returned by `Storage.getAllSecurities()` so the
         * Send / Receive stock drawer entries (see
         * `docs/exec-plans/active/2026-06-08-stock-transfer-ui.md`) are
         * gated correctly.
         *
         * @param {object} context - Vuex action context
         * @param {string} security - permission key to check
         *      (e.g. 'StockTake', 'StockTransfer', 'VoidSale',
         *      'BelowCostSale'). The lookup is case-insensitive against
         *      the user's `Type`; the permission key is read as-is from
         *      the group document.
         * @returns {Promise<boolean|{supervisor: string}>} Resolves as
         *      follows:
         *      - `true` — the user is allowed outright.
         *      - `{ supervisor: <username> }` — the permission was a
         *        void-style flow; caller should stamp the void record
         *        with the authorising cashier username.
         *      - `false` — denied: either no security group, or the
         *        supervisor modal was dismissed / cancelled / PIN
         *        verification failed.
         *
         * @see {@link ./user/user_security_module.js#checkUserSecurity}
         *      for the action that opens the supervisor PIN modal.
         * @see {@link ../components/UserSecurityChecker.vue} for the
         *      Supervisor Approval Required modal markup and the
         *      `checkSecurity()` handler that validates the supervisor's
         *      PIN.
         * @see {@link ../components/security/AdminSupportStaffModal.vue}
         *      for the admin-only support-staff picker + OTP gate that
         *      fires when the resolved supervisor's username is
         *      `Admin`.
         */
        validateSecurityPermission: async (context, security) => {
            let securities = await Storage.getAllSecurities();
            let user = await Storage.getCurrentUser();
            let userType = user.Type;
            let group = securities.find(sec => sec.GroupName.toLowerCase() === userType.toLowerCase());
            if (!group) {
                await responses.showWarning(`No security group found`)
                return false;
            }
            if (!group[security]) {
                $utils.hideLoading();
                return context.dispatch('checkUserSecurity', security).then(async (result) => {
                    if (result.isConfirmed) {
                        let value = result.value;
                        if (value.allowed) {
                            let store = await Storage.getDefaultStore();
                            await Storage.setSupervisor(value.supervisor);
                            axios.post(serverApi + `supervisorOverrides/${store.StoreDB}`, {
                                Supervisor: value.supervisor,
                                Cashier: value.cashier,
                                Reason: security,
                            })
                        }
                        return value.allowed;
                    }
                    return false;
                });

            }
            if (security.toString().toLowerCase().includes(`void`)) {
                return { supervisor: user.Username }
            }
            return true;
        },

        async getAllSecurities(context) {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            
            // Check if online
            const isOnline = connectivityMonitor.getStatus();
            
            if (!isOnline) {
                // Offline: Load from cache
                //console.log('[User Module] Offline - loading securities from cache');
                const cachedSecurities = await Storage.getAllSecurities();
                if (cachedSecurities && cachedSecurities.length > 0) {
                    context.commit('setAllSecurities', cachedSecurities);
                    return cachedSecurities;
                } else {
                    responses.showWarning('No cached security data available. Please connect to network.');
                    return [];
                }
            }
            
            // Online: Fetch from server
            return axios.get(franchiseTomcatApi + `empsecurity/${store.StoreDB}/getAll`)
                .then(async ({ data }) => {
                    await Storage.setAllSecurities(data);
                    context.commit(`setAllSecurities`, data);
                    return data;
                })
                .catch(async (err) => {
                    // Network error: Fallback to cache
                    console.warn('[User Module] Failed to fetch securities from server, using cache');
                    const cachedSecurities = await Storage.getAllSecurities();
                    if (cachedSecurities && cachedSecurities.length > 0) {
                        context.commit('setAllSecurities', cachedSecurities);
                        Toast.fire({
                            icon: 'warning',
                            title: 'Using cached security data'
                        });
                        return cachedSecurities;
                    }
                    return errorHandler.tomcatError(err);
                })
        },

    }
}
